"use client";

import type { AssetFilter, AssetHistoryItem } from "@saas/shared/history/types";
import { GENERATION_HISTORY_QUERY_KEY } from "@saas/shared/history/types";
import { apiClient } from "@shared/lib/api-client";
import { useInfiniteQuery } from "@tanstack/react-query";

/** A single pickable image: one generated output, or one uploaded reference. */
export interface AssetItem {
	/** Stable key — a generation row can hold several images */
	key: string;
	url: string;
	kind: "creation" | "upload";
	createdAt: string;
	prompt?: string;
}

const PAGE_SIZE = 24;

/**
 * Backs the asset picker. Filtering happens server-side (see the `filter` query
 * param) so the infinite scroll's page count stays honest.
 */
export function useAssetLibrary({
	filter,
	enabled = true,
}: {
	filter: AssetFilter;
	enabled?: boolean;
}) {
	const query = useInfiniteQuery({
		// Shares the history prefix so generating or uploading invalidates this too
		queryKey: [...GENERATION_HISTORY_QUERY_KEY, "asset-picker", filter],
		enabled,
		queryFn: async ({ pageParam }) => {
			const response = await apiClient.generations.history.$get({
				query: {
					page: String(pageParam),
					limit: String(PAGE_SIZE),
					includeUploads: "true",
					excludeVideos: "true",
					filter,
				},
			});
			if (!response.ok) {
				throw new Error("Failed to fetch asset library");
			}
			return await response.json();
		},
		initialPageParam: 1,
		getNextPageParam: (lastPage) =>
			lastPage.pagination.page < lastPage.pagination.totalPages
				? lastPage.pagination.page + 1
				: undefined,
	});

	const items: AssetItem[] = (query.data?.pages ?? [])
		.flatMap((page) => page.data as AssetHistoryItem[])
		.flatMap((item): AssetItem[] => {
			if (item.type === "upload") {
				return [
					{
						key: `upload-${item.id}`,
						url: item.imageUrl,
						kind: "upload",
						createdAt: item.createdAt,
					},
				];
			}
			if (item.type === "image") {
				return (
					item.imageUrls
						.map((url, index) => ({
							key: `image-${item.id}-${index}`,
							url,
							kind: "creation" as const,
							createdAt: item.createdAt,
							prompt: item.originalPrompt,
						}))
						// 收藏是单张图级别的，Favorite tab 只留被收藏的那几张
						.filter(
							(_, index) =>
								filter !== "favorite" ||
								!!item.favoriteImageIndexes?.includes(index),
						)
				);
			}
			// `excludeVideos` means videos never get here
			return [];
		});

	return { ...query, items };
}

/** Same "MM-DD, YYYY" heading My Assets uses. The feed arrives sorted desc, so
 *  insertion order is already newest-first. */
export function groupAssetsByDate(
	items: AssetItem[],
): { date: string; items: AssetItem[] }[] {
	const groups: { date: string; items: AssetItem[] }[] = [];

	for (const item of items) {
		const date = new Date(item.createdAt);
		const label = `${String(date.getMonth() + 1).padStart(2, "0")}-${String(
			date.getDate(),
		).padStart(2, "0")}, ${date.getFullYear()}`;
		const group = groups.find((entry) => entry.date === label);
		if (group) {
			group.items.push(item);
		} else {
			groups.push({ date: label, items: [item] });
		}
	}

	return groups;
}
